import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Building2, X, Check, Mail } from "lucide-react";

export default function InvitationInbox({ onChange }) {
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/invitations/inbox");
      setInvites(data || []);
      if ((data || []).length > 0) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // poll every 30s
    return () => clearInterval(id);
  }, [load]);

  const decide = async (token, action) => {
    setErr("");
    setBusy(token + action);
    try {
      await api.post(`/invitations/${action}`, { token });
      await load();
      if (onChange) onChange();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  };

  if (invites.length === 0 || !open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4 sm:p-8" data-testid="invitation-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg border border-zinc-200 overflow-hidden">
        <div className="p-6 border-b border-zinc-100 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "var(--gold-light)" }}>
              <Mail className="w-5 h-5" style={{ color: "var(--gold-hover)" }} />
            </span>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-500">{invites.length === 1 ? "New invitation" : `${invites.length} new invitations`}</div>
              <h2 className="font-bold text-xl mt-0.5">You&apos;ve been invited to a clinic</h2>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900" data-testid="invitation-dismiss"><X className="w-5 h-5" /></button>
        </div>

        <div className="divide-y divide-zinc-100 max-h-[60vh] overflow-y-auto">
          {invites.map((inv) => (
            <div key={inv.token} className="p-6" data-testid={`invite-card-${inv.token}`}>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 rounded-md flex items-center justify-center bg-zinc-100">
                  <Building2 className="w-5 h-5 text-zinc-700" />
                </span>
                <div className="min-w-0">
                  <div className="font-bold text-lg" data-testid={`invite-clinic-${inv.token}`}>{inv.clinic_name}</div>
                  {inv.clinic_location && <div className="text-sm text-zinc-500">{inv.clinic_location}</div>}
                  <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
                    Accepting links your account to <strong>{inv.clinic_name}</strong>. Your personal pain data
                    stays private — your manager only sees anonymized aggregates.
                  </p>
                </div>
              </div>
              {err && <div className="text-sm text-red-600 mt-3">{err}</div>}
              <div className="flex gap-2 mt-5">
                <button
                  onClick={() => decide(inv.token, "accept")}
                  disabled={busy.startsWith(inv.token)}
                  className="btn-gold flex-1 h-11"
                  data-testid={`invite-accept-${inv.token}`}
                >
                  <Check className="w-4 h-4 mr-2" />{busy === inv.token + "accept" ? "Joining\u2026" : "Accept and join"}
                </button>
                <button
                  onClick={() => decide(inv.token, "decline")}
                  disabled={busy.startsWith(inv.token)}
                  className="btn-outline h-11 px-5"
                  data-testid={`invite-decline-${inv.token}`}
                >
                  {busy === inv.token + "decline" ? "Declining\u2026" : "Decline"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
